import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Tag, X } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const inputCls = "w-full rounded-lg bg-ink-900 border border-white/12 px-3.5 py-2.5 text-ice placeholder:text-mist focus:border-cyan/60 outline-none text-sm";
const blank = () => ({ id: null, name: "", price: "", credits: 5, valid_weeks: 6, description: "", location_id: "", published: true });

function Field({ label, children }) {
  return <label className="block"><span className="label block mb-1.5">{label}</span>{children}</label>;
}

function BundleForm({ initial, locations, onClose, onSaved }) {
  const [b, setB] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setB({ ...b, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    if (!b.name.trim()) return setErr("Please give the bundle a name.");
    if (!(Number(b.price) > 0)) return setErr("Price must be more than £0.");
    if (!(Number(b.credits) >= 2)) return setErr("A bundle needs at least 2 classes.");
    if (!(Number(b.valid_weeks) >= 1)) return setErr("Validity must be at least 1 week.");
    setBusy(true); setErr("");
    const body = { ...b, price: Number(b.price), credits: Number(b.credits), valid_weeks: Number(b.valid_weeks), location_id: b.location_id || null };
    try {
      await (b.id ? api.put(`/admin/bundles/${b.id}`, body) : api.post("/admin/bundles", body));
      onSaved();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="bundle-form" className="rounded-xl border border-cyan/30 bg-ink-800 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-white">{b.id ? "Edit bundle" : "New bundle"}</h3>
        <button data-testid="bundle-form-close" onClick={onClose} className="text-ice/60 hover:text-white"><X size={18} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name"><input data-testid="bundle-name" className={inputCls} value={b.name} onChange={set("name")} placeholder="5-class bundle" /></Field>
        <Field label="Location">
          <select data-testid="bundle-location" className={inputCls} value={b.location_id || ""} onChange={set("location_id")}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Price (£)"><input data-testid="bundle-price" type="number" min="1" className={inputCls} value={b.price} onChange={set("price")} /></Field>
        <Field label="Classes (credits)"><input data-testid="bundle-credits" type="number" min="2" className={inputCls} value={b.credits} onChange={set("credits")} /></Field>
        <Field label="Valid for (weeks)"><input data-testid="bundle-weeks" type="number" min="1" className={inputCls} value={b.valid_weeks} onChange={set("valid_weeks")} /></Field>
        <label className="flex items-center gap-2 text-sm text-ice/80 mt-6">
          <input data-testid="bundle-published" type="checkbox" checked={b.published} onChange={set("published")} className="accent-cyan h-4 w-4" /> Show on the website
        </label>
      </div>
      <Field label="Description"><textarea data-testid="bundle-description" rows={2} className={inputCls} value={b.description} onChange={set("description")} /></Field>
      {err && <p className="text-red-300 text-sm" data-testid="bundle-error">{err}</p>}
      <button data-testid="bundle-save" onClick={save} disabled={busy} className="book-cta disabled:opacity-60">
        {busy ? <Loader2 size={16} className="animate-spin" /> : "Save bundle"}
      </button>
    </div>
  );
}

export function BundlesManager() {
  const [rows, setRows] = useState(null);
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/bundles").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/admin/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  const locName = (b) => (b.location_id ? b.location_name || "Location removed" : "All locations");
  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.name}"? Codes already sold keep working.`)) return;
    await api.delete(`/admin/bundles/${b.id}`); load();
  };
  if (!rows) return <Loader2 className="animate-spin text-cyan" />;
  return (
    <div className="space-y-4" data-testid="admin-bundles">
      <div className="flex items-center justify-between gap-4">
        <p className="text-ice/70 text-sm max-w-xl">Each bundle is bought for one location and its credits only work there. Bundles sold before this change still work at any location.</p>
        <button data-testid="bundle-add" onClick={() => setEditing(blank())} className="book-cta shrink-0"><Plus size={16} /> Add bundle</button>
      </div>
      {editing && <BundleForm key={editing.id || "new"} initial={editing} locations={locations} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((b) => (
          <div key={b.id} data-testid={`admin-bundle-${b.id}`} className="rounded-xl border border-white/8 bg-ink-800 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-semibold flex items-center gap-2"><Tag size={15} className="text-cyan" /> {b.name}</p>
                <p className="text-cyan text-xs mt-1">{locName(b)}</p>
              </div>
              <p className="font-display text-2xl font-bold text-white">£{b.price}</p>
            </div>
            <p className="text-ice/70 text-sm mt-3">{b.credits} classes · {b.valid_weeks} weeks · {b.sold} sold{b.published === false ? " · Hidden" : ""}</p>
            <div className="flex gap-3 mt-4">
              <button data-testid={`bundle-edit-${b.id}`} onClick={() => setEditing({ ...b, location_id: b.location_id || "" })} className="text-xs text-ice/70 hover:text-cyan flex items-center gap-1"><Pencil size={13} /> Edit</button>
              <button data-testid={`bundle-delete-${b.id}`} onClick={() => remove(b)} className="text-xs text-ice/70 hover:text-red-300 flex items-center gap-1"><Trash2 size={13} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      {rows.length === 0 && <p className="text-mist">No bundles yet.</p>}
    </div>
  );
}
